<template>
  <div ref="formRoot" class="ad-form">
    <div class="ad-section">
      <div class="ad-section-header">
        <Icon icon="mdi:domain" width="16" color="#888" />
        <span>Company</span>
      </div>
      <v-row dense>
        <v-col cols="12" md="8">
          <v-text-field
            v-model="model.companyName"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('companyName')"
            data-required-key="companyName"
            class="ad-required-field"
          >
            <template #label>Company name <span class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <div class="ad-section">
      <div class="ad-section-header">
        <Icon icon="mdi:file-document-outline" width="16" color="#888" />
        <span>Notice</span>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-menu v-model="noticeDateMenu" :close-on-content-click="false" location="bottom" :disabled="!!readOnly">
            <template #activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                v-model="noticeDateDisplay"
                :placeholder="noticeDatePlaceholder"
                variant="outlined"
                density="compact"
                hide-details="auto"
                readonly
                append-inner-icon="mdi-calendar"
                :disabled="!!readOnly"
                :error="hasRequiredError('noticeDate')"
                data-required-key="noticeDate"
                class="ad-required-field"
              >
                <template #label>Notice date <span class="ad-required-mark">*</span></template>
              </v-text-field>
            </template>
            <v-date-picker
              v-bind="noticeDatePickerProps"
              v-model="noticeDatePickerValue"
              @update:model-value="noticeDateMenu = false"
            />
          </v-menu>
          <v-chip
            v-if="shouldShowDateSuggestion('noticeDate', suggestedToday)"
            size="x-small"
            variant="outlined"
            color="warning"
            prepend-icon="mdi-lightbulb-outline"
            closable
            close-icon="mdi-close"
            class="mt-1 ad-suggest-chip"
            @mousedown.stop.prevent
            @click.stop.prevent="applySuggestedDate('noticeDate', suggestedToday)"
            @click:close.stop.prevent="dismissSuggestion('noticeDate', suggestedToday)"
          >
            Today: {{ formatIsoForSession(suggestedToday) }}
          </v-chip>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.noticeRecipientName"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('noticeRecipientName')"
            data-required-key="noticeRecipientName"
            class="ad-required-field"
          >
            <template #label>Notice recipient name <span class="ad-required-mark">*</span></template>
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn
                    v-bind="tProps"
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="!!readOnly"
                    @click.stop="requestNoticeRecipientLookup"
                  />
                </template>
                <span>Lookup global contact from chat</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12">
          <v-text-field
            v-model="model.replyToAddress"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('replyToAddress')"
            data-required-key="replyToAddress"
            class="ad-required-field"
          >
            <template #label>Reply-to address <span class="ad-required-mark">*</span></template>
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn
                    v-bind="tProps"
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="!!readOnly"
                    @click.stop="requestReplyToAddressInput"
                  />
                </template>
                <span>Enter Singapore address in chat</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.noticeSignerName"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('noticeSignerName')"
            data-required-key="noticeSignerName"
            class="ad-required-field"
          >
            <template #label>Notice signer name <span class="ad-required-mark">*</span></template>
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn
                    v-bind="tProps"
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="!!readOnly"
                    @click.stop="requestNoticeSignerLookup"
                  />
                </template>
                <span>Lookup company secretary from chat</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>

    <div class="ad-section">
      <div class="ad-section-header">
        <Icon icon="mdi:office-building" width="16" color="#888" />
        <span>Corporate Controller</span>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.controllerCorporateName"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('controllerCorporateName')"
            data-required-key="controllerCorporateName"
            class="ad-required-field"
          >
            <template #label>Controller corporate name <span class="ad-required-mark">*</span></template>
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn
                    v-bind="tProps"
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="!!readOnly"
                    @click.stop="requestCorporateControllerLookup"
                  />
                </template>
                <span>Lookup corporate contact from chat</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.controllerUen"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="controllerUenRequired && hasRequiredError('controllerUen')"
            data-required-key="controllerUen"
            :class="{ 'ad-required-field': controllerUenRequired }"
          >
            <template #label>Controller UEN <span v-if="controllerUenRequired" class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
      </v-row>
      <NoticeCorporateContactProfileFields
        :model="model"
        :read-only="!!readOnly"
        @request-address-lookup="(ctx) => emit('request-address-lookup', ctx)"
      />
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.controllerEmail"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('controllerEmail')"
            data-required-key="controllerEmail"
            class="ad-required-field"
          >
            <template #label>Email <span class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-menu v-model="becameDateMenu" :close-on-content-click="false" location="bottom" :disabled="!!readOnly">
            <template #activator="{ props: menuProps }">
              <v-text-field
                v-bind="menuProps"
                v-model="becameDateDisplay"
                :placeholder="becameDatePlaceholder"
                variant="outlined"
                density="compact"
                hide-details="auto"
                readonly
                append-inner-icon="mdi-calendar"
                :disabled="!!readOnly"
                :error="hasRequiredError('controllerBecameDate')"
                data-required-key="controllerBecameDate"
                class="ad-required-field"
              >
                <template #label>Date became controller <span class="ad-required-mark">*</span></template>
              </v-text-field>
            </template>
            <v-date-picker
              v-bind="becameDatePickerProps"
              v-model="becameDatePickerValue"
              @update:model-value="becameDateMenu = false"
            />
          </v-menu>
          <v-chip
            v-if="shouldShowDateSuggestion('controllerBecameDate', suggestedToday)"
            size="x-small"
            variant="outlined"
            color="warning"
            prepend-icon="mdi-lightbulb-outline"
            closable
            close-icon="mdi-close"
            class="mt-1 ad-suggest-chip"
            @mousedown.stop.prevent
            @click.stop.prevent="applySuggestedDate('controllerBecameDate', suggestedToday)"
            @click:close.stop.prevent="dismissSuggestion('controllerBecameDate', suggestedToday)"
          >
            Today: {{ formatIsoForSession(suggestedToday) }}
          </v-chip>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.controllerLegalForm"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('controllerLegalForm')"
            data-required-key="controllerLegalForm"
            class="ad-required-field"
          >
            <template #label>Legal form <span class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-autocomplete
            v-model="model.controllerJurisdictionCd"
            :items="jurisdictionOptions"
            item-title="title"
            item-value="value"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            clearable
            :error="hasRequiredError('controllerJurisdictionCd')"
            data-required-key="controllerJurisdictionCd"
            class="ad-required-field"
          >
            <template #label>Jurisdiction where the corporate controller is formed <span class="ad-required-mark">*</span></template>
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn
                    v-bind="tProps"
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="!!readOnly"
                    @click.stop="requestJurisdictionLookup"
                  />
                </template>
                <span>Select jurisdiction from chat</span>
              </v-tooltip>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.controllerIncorporationStatute"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('controllerIncorporationStatute')"
            data-required-key="controllerIncorporationStatute"
            class="ad-required-field"
          >
            <template #label>Statute where the corporate controller is formed or incorporated <span class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.controllerRegisterName"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('controllerRegisterName')"
            data-required-key="controllerRegisterName"
            class="ad-required-field"
          >
            <template #label>Corporate entity register <span class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.controllerRegistrationNo"
            label="Registration no"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
          />
        </v-col>
      </v-row>
    </div>

    <div class="ad-section">
      <div class="ad-section-header">
        <Icon icon="mdi:draw-pen" width="16" color="#888" />
        <span>Acknowledgement</span>
      </div>
      <v-row dense>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.acknowledgementSignerName"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('acknowledgementSignerName')"
            data-required-key="acknowledgementSignerName"
            class="ad-required-field"
          >
            <template #label>Acknowledgement signer name <span class="ad-required-mark">*</span></template>
            <template #append-inner>
              <v-tooltip location="top" :open-delay="150">
                <template #activator="{ props: tProps }">
                  <v-btn
                    v-bind="tProps"
                    icon="mdi-creation"
                    size="x-small"
                    variant="text"
                    color="primary"
                    :disabled="!!readOnly"
                    @click.stop="requestAcknowledgementSignerLookup"
                  />
                </template>
                <span>Lookup officer from chat</span>
              </v-tooltip>
            </template>
          </v-text-field>
        </v-col>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="model.acknowledgementSignerRole"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="!!readOnly"
            :error="hasRequiredError('acknowledgementSignerRole')"
            data-required-key="acknowledgementSignerRole"
            class="ad-required-field"
          >
            <template #label>Acknowledgement signer role <span class="ad-required-mark">*</span></template>
          </v-text-field>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { Icon } from '@iconify/vue';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import {
  getCurrentDateByJurisdiction,
  useDateField,
  useFormatDateByUserFormat,
} from '@/shared/composables/dateUtils';
import type { NoticeCorporateFormModel } from './composables/useFormPayload';
import NoticeCorporateContactProfileFields from './NoticeCorporateContactProfileFields.vue';
import { docGenApi } from '@/features/AI-documents/api/docGenApi';
import {
  ensureB15JurisdictionNameMap,
  isSgControllerJurisdiction,
  NOTICE_CORPORATE_CONTACT_CATEGORY_CD,
  resolveB15CodeToName,
} from './composables/b15JurisdictionDisplay';
import {
  loadB15JurisdictionOptions,
  type CodeSelectOption,
} from './composables/formOptions';
import {
  migrateLegacyNoticeCorporateFields,
} from './composables/useFormPayload';

type NoticeDateField = 'noticeDate' | 'controllerBecameDate';

const props = withDefaults(
  defineProps<{
    model: NoticeCorporateFormModel;
    entityCd?: string;
    readOnly?: boolean;
    selectedPerson?: OfficerDetail | null;
    activePersonContext?: Record<string, unknown> | null;
  }>(),
  {
    entityCd: '',
    readOnly: false,
    selectedPerson: null,
    activePersonContext: null,
  },
);

const emit = defineEmits<{
  (e: 'request-person-lookup', context: Record<string, unknown>): void;
  (e: 'request-address-lookup', context: Record<string, unknown>): void;
}>();

const formRoot = ref<HTMLElement | null>(null);
const requiredErrorKeys = ref<Set<string>>(new Set());
const b15JurisdictionMap = ref<Map<string, string>>(new Map());
const jurisdictionOptions = ref<CodeSelectOption[]>([]);
const lastContactRegNo = ref('');

const controllerUenRequired = computed(() =>
  isSgControllerJurisdiction(trim(props.model.controllerJurisdictionCd)),
);

onMounted(async () => {
  b15JurisdictionMap.value = await ensureB15JurisdictionNameMap();
  jurisdictionOptions.value = await loadB15JurisdictionOptions();
  migrateLegacyNoticeCorporateFields(props.model, b15JurisdictionMap.value);
  if (trim(props.model.controllerJurisdictionCd)) {
    props.model.controllerJurisdiction = resolveB15CodeToName(
      props.model.controllerJurisdictionCd,
      b15JurisdictionMap.value,
    );
  }
});

watch(
  () => props.model.controllerJurisdictionCd,
  (cd) => {
    if (!cd) return;
    props.model.controllerJurisdiction = resolveB15CodeToName(cd, b15JurisdictionMap.value);
    syncControllerIdentityFields();
  },
);

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

function hasRequiredError(key: string): boolean {
  return requiredErrorKeys.value.has(key);
}

function setRequiredErrors(keys: string[]) {
  requiredErrorKeys.value = new Set(keys);
}

function firstMissingRequiredKeys(): string[] {
  const missing: string[] = [];
  const pushIfEmpty = (key: string, value: unknown) => {
    if (!trim(value)) missing.push(key);
  };

  pushIfEmpty('companyName', props.model.companyName);
  pushIfEmpty('noticeDate', props.model.noticeDate);
  pushIfEmpty(
    'noticeRecipientName',
    trim(props.model.noticeRecipientName) || trim(props.model.controllerCorporateName),
  );
  pushIfEmpty('replyToAddress', props.model.replyToAddress);
  pushIfEmpty('noticeSignerName', props.model.noticeSignerName);
  pushIfEmpty('controllerCorporateName', props.model.controllerCorporateName);
  if (isSgControllerJurisdiction(trim(props.model.controllerJurisdictionCd))) {
    pushIfEmpty('controllerUen', props.model.controllerUen);
  }
  pushIfEmpty('controllerRegisteredOffice', props.model.controllerRegisteredOffice);
  pushIfEmpty('controllerEmail', props.model.controllerEmail);
  pushIfEmpty('controllerCountryCd', props.model.controllerCountryCd);
  pushIfEmpty('controllerMobileNo', props.model.controllerMobileNo);
  pushIfEmpty('controllerLegalForm', props.model.controllerLegalForm);
  pushIfEmpty('controllerJurisdictionCd', props.model.controllerJurisdictionCd);
  pushIfEmpty('controllerIncorporationStatute', props.model.controllerIncorporationStatute);
  pushIfEmpty('controllerRegisterName', props.model.controllerRegisterName);
  pushIfEmpty('controllerBecameDate', props.model.controllerBecameDate);
  pushIfEmpty('acknowledgementSignerName', props.model.acknowledgementSignerName);
  pushIfEmpty('acknowledgementSignerRole', props.model.acknowledgementSignerRole);

  return missing;
}

async function focusRequiredField(key: string): Promise<void> {
  await nextTick();
  const root = formRoot.value;
  if (!root) return;
  const target = root.querySelector<HTMLElement>(`[data-required-key="${key}"]`);
  if (!target) return;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const focusable = target.querySelector<HTMLElement>('input, textarea, [role="combobox"], button');
  focusable?.focus();
}

async function validateAndFocusRequiredFields(): Promise<boolean> {
  const missingKeys = firstMissingRequiredKeys();
  setRequiredErrors(missingKeys);
  if (missingKeys.length === 0) return true;
  await focusRequiredField(missingKeys[0]);
  return false;
}

defineExpose({ validateAndFocusRequiredFields });

const formatDateByUserFormat = useFormatDateByUserFormat();
const suggestedToday = computed(() => getCurrentDateByJurisdiction());
const dismissedSuggestions = ref<Set<string>>(new Set());

function bindModelDate(
  get: () => string,
  set: (v: string) => void,
  field: ReturnType<typeof useDateField>,
) {
  watch(get, (v) => {
    const s = trim(v);
    if (field.dateValue.value !== s) field.dateValue.value = s;
  }, { immediate: true });
  watch(field.dateValue, (v) => {
    const s = trim(v);
    if (trim(get()) !== s) set(s);
  });
}

const noticeDate = useDateField();
bindModelDate(() => props.model.noticeDate, (v) => { props.model.noticeDate = v; }, noticeDate);
const noticeDateMenu = ref(false);
const noticeDateDisplay = noticeDate.dateDisplay;
const noticeDatePickerValue = noticeDate.datePickerValue;
const noticeDatePlaceholder = noticeDate.datePlaceholder;
const noticeDatePickerProps = noticeDate.datePickerProps;

const becameDate = useDateField();
bindModelDate(() => props.model.controllerBecameDate, (v) => { props.model.controllerBecameDate = v; }, becameDate);
const becameDateMenu = ref(false);
const becameDateDisplay = becameDate.dateDisplay;
const becameDatePickerValue = becameDate.datePickerValue;
const becameDatePlaceholder = becameDate.datePlaceholder;
const becameDatePickerProps = becameDate.datePickerProps;

function suggestionKey(field: NoticeDateField, value: string): string {
  return `${field}:${value}`;
}

function currentDateValue(field: NoticeDateField): string {
  if (field === 'noticeDate') return trim(props.model.noticeDate);
  if (field === 'controllerBecameDate') return trim(props.model.controllerBecameDate);
  return '';
}

function isSuggestionDismissed(field: NoticeDateField, value: string): boolean {
  if (!value) return true;
  return dismissedSuggestions.value.has(suggestionKey(field, value));
}

function dismissSuggestion(field: NoticeDateField, value: string) {
  if (!value) return;
  const next = new Set(dismissedSuggestions.value);
  next.add(suggestionKey(field, value));
  dismissedSuggestions.value = next;
}

function shouldShowDateSuggestion(field: NoticeDateField, value: string): boolean {
  if (!value) return false;
  return currentDateValue(field) !== value && !isSuggestionDismissed(field, value);
}

function applySuggestedDate(field: NoticeDateField, value: string) {
  if (!value) return;
  if (field === 'noticeDate') {
    props.model.noticeDate = value;
    noticeDate.dateValue.value = value;
  } else if (field === 'controllerBecameDate') {
    props.model.controllerBecameDate = value;
    becameDate.dateValue.value = value;
  }
}

function formatIsoForSession(iso: string): string {
  if (!iso) return '';
  const m = iso.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (!m) return iso;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0);
  if (isNaN(d.getTime())) return iso;
  return formatDateByUserFormat(d);
}

function requestNoticeRecipientLookup() {
  if (props.readOnly) return;
  emit('request-person-lookup', {
    fieldGroup: 'noticeRecipient',
    defaultRole: 'CONTACT',
    categoryCd: NOTICE_CORPORATE_CONTACT_CATEGORY_CD,
  });
}

function requestCorporateControllerLookup() {
  if (props.readOnly) return;
  emit('request-person-lookup', {
    fieldGroup: 'corporateController',
    defaultRole: 'CONTACT',
    categoryCd: NOTICE_CORPORATE_CONTACT_CATEGORY_CD,
  });
}

function requestJurisdictionLookup() {
  if (props.readOnly) return;
  emit('request-person-lookup', { fieldGroup: 'controllerJurisdictionB15' });
}

function syncControllerIdentityFields(contactRegNo?: string) {
  if (contactRegNo !== undefined && trim(contactRegNo)) {
    lastContactRegNo.value = trim(contactRegNo);
  }

  const contactReg = (contactRegNo !== undefined ? trim(contactRegNo) : '') || lastContactRegNo.value;
  if (contactReg) {
    props.model.controllerRegistrationNo = contactReg;
  }

  const cd = trim(props.model.controllerJurisdictionCd).toUpperCase();
  if (!cd) return;

  if (isSgControllerJurisdiction(cd)) {
    props.model.controllerUen = contactReg;
  } else {
    props.model.controllerUen = '';
  }
}

function applySelectedJurisdiction(selection: Record<string, unknown>) {
  const code = trim(selection.totalCode ?? selection.NATIONALITY_CD).toUpperCase();
  const name = trim(selection.codeName ?? selection.name ?? selection.title);
  props.model.controllerJurisdictionCd = code;
  props.model.controllerJurisdiction = name || resolveB15CodeToName(code, b15JurisdictionMap.value);
  syncControllerIdentityFields();
}

function requestNoticeSignerLookup() {
  if (props.readOnly) return;
  emit('request-person-lookup', { fieldGroup: 'noticeSigner', defaultRole: 'SECRETARY' });
}

function requestAcknowledgementSignerLookup() {
  if (props.readOnly) return;
  emit('request-person-lookup', { fieldGroup: 'acknowledgementSigner' });
}

function requestReplyToAddressInput() {
  if (props.readOnly) return;
  emit('request-address-lookup', {
    fieldGroup: 'replyToAddress',
    index: 0,
    addresses: [],
    directInput: true,
    subjectLabel: 'Reply-to address',
  });
}

function resolvePersonAddress(person: OfficerDetail): string {
  const raw = person as unknown as Record<string, unknown>;
  const direct = trim(raw.primaryAddress ?? raw.address ?? raw.residentialAddress);
  if (direct) return direct;
  const block = trim(raw.blockNo);
  const street = trim(raw.streetName);
  const postal = trim(raw.postalCode);
  const line = [block, street].filter(Boolean).join(' ').trim();
  if (line && postal) return `${line}, SINGAPORE ${postal}`;
  return line;
}

function resolvePersonAlternateAddress(person: OfficerDetail): string {
  const raw = person as unknown as Record<string, unknown>;
  const direct = trim(raw.alternateAddress ?? raw.altAddress ?? raw.secondaryAddress);
  if (direct) return direct;
  const block = trim(raw.altBlockNo ?? raw.ALT_BLOCK_NO);
  const street = trim(raw.altStreetName ?? raw.ALT_STREET_NAME);
  const postal = trim(raw.altPostalCode ?? raw.ALT_POSTAL_CODE);
  const line = [block, street].filter(Boolean).join(' ').trim();
  if (line && postal) return `${line}, SINGAPORE ${postal}`;
  return line;
}

function applyCorporateController(person: OfficerDetail) {
  const raw = person as unknown as Record<string, unknown>;
  const name = trim(person.name ?? person.contactName);
  const contactRegNo = trim(raw.contactRegNo ?? raw.CONTACT_REG_NO ?? person.contactRegNo);
  const email = trim(raw.contactEmail ?? person.contactEmail);
  const mobile = trim(raw.mobileNo ?? person.mobileNo);
  const address = resolvePersonAddress(person);
  const alternateAddress = resolvePersonAlternateAddress(person);
  const legalForm = trim(raw.legalForm ?? raw.LEGAL_FORM);
  const jurisdictionRaw = trim(raw.jurisdiction ?? raw.JURISDICTION);
  const statute = trim(raw.incorporationStatute ?? raw.INCORPORATION_STATUTE);
  const countryCd = trim(raw.countryCd ?? raw.COUNTRY_CD);
  const areaCode = trim(raw.areaCode ?? raw.AREA_CODE);
  const mobileNo = trim(raw.mobileNo ?? raw.MOBILE_NO ?? raw.contactNo);

  if (name) {
    props.model.controllerCorporateName = name;
    if (!trim(props.model.noticeRecipientName)) props.model.noticeRecipientName = name;
  }
  if (address) props.model.controllerRegisteredOffice = address;
  if (alternateAddress) props.model.controllerAlternateAddress = alternateAddress;
  if (email) props.model.controllerEmail = email;
  if (countryCd) props.model.controllerCountryCd = countryCd;
  if (areaCode) props.model.controllerAreaCode = areaCode;
  if (mobileNo) props.model.controllerMobileNo = mobileNo;
  else if (mobile) props.model.controllerMobileNo = mobile;
  if (legalForm) props.model.controllerLegalForm = legalForm;
  if (jurisdictionRaw) {
    props.model.controllerJurisdictionCd = jurisdictionRaw.toUpperCase();
    props.model.controllerJurisdiction = resolveB15CodeToName(
      jurisdictionRaw,
      b15JurisdictionMap.value,
    );
  }
  if (statute) props.model.controllerIncorporationStatute = statute;
  const registerName = trim(raw.corporateEntityRegister ?? raw.CORPORATE_ENTITY_REGISTER);
  if (registerName) props.model.controllerRegisterName = registerName;
  syncControllerIdentityFields(contactRegNo);
  props.model.contactCd = trim(person.contactCd);
}

function applySelectedSecretary(person: OfficerDetail) {
  const fullName = trim(person.name ?? person.contactName);
  if (fullName) props.model.noticeSignerName = fullName;
}

function applyAcknowledgementSigner(person: OfficerDetail) {
  const raw = person as unknown as Record<string, unknown>;
  const fullName = trim(person.name ?? person.contactName);
  let role = trim(raw.position ?? raw.positionName ?? raw.role ?? '');
  if (!role) {
    const positions = raw.positions;
    if (Array.isArray(positions) && positions.length > 0) {
      role = positions
        .map((p) => trim((p as Record<string, unknown>)?.positionName))
        .filter(Boolean)
        .join(', ');
    }
  }
  if (fullName) props.model.acknowledgementSignerName = fullName;
  if (role) props.model.acknowledgementSignerRole = role;
}

async function hydrateCorporateContactIfMissing(person: OfficerDetail) {
  const entityCd = trim(props.entityCd);
  const contactCd = trim(person.contactCd);
  if (!entityCd || !contactCd) return;
  try {
    const response: unknown = await docGenApi.getContactDetail(entityCd, contactCd);
    const detail = (response as { data?: { data?: Record<string, unknown> } })?.data?.data
      ?? (response as { data?: Record<string, unknown> })?.data
      ?? response;
    if (!detail || typeof detail !== 'object') return;
    const d = detail as Record<string, unknown>;
    const contactRegNo = trim(d.CONTACT_REG_NO ?? d.contactRegNo);
    if (!trim(props.model.controllerCountryCd)) {
      const c = trim(d.COUNTRY_CD ?? d.countryCd);
      if (c) props.model.controllerCountryCd = c;
    }
    if (!trim(props.model.controllerAreaCode)) {
      const a = trim(d.AREA_CODE ?? d.areaCode);
      if (a) props.model.controllerAreaCode = a;
    }
    if (!trim(props.model.controllerMobileNo)) {
      const m = trim(d.MOBILE_NO ?? d.mobileNo);
      if (m) props.model.controllerMobileNo = m;
    }
    if (!trim(props.model.controllerLegalForm)) {
      const lf = trim(d.LEGAL_FORM ?? d.legalForm);
      if (lf) props.model.controllerLegalForm = lf;
    }
    const j = trim(d.JURISDICTION ?? d.jurisdiction);
    const s = trim(d.INCORPORATION_STATUTE ?? d.incorporationStatute);
    if (!trim(props.model.controllerJurisdictionCd) && j) {
      props.model.controllerJurisdictionCd = j.toUpperCase();
      props.model.controllerJurisdiction = resolveB15CodeToName(j, b15JurisdictionMap.value);
    }
    if (!trim(props.model.controllerIncorporationStatute) && s) {
      props.model.controllerIncorporationStatute = s;
    }
    if (!trim(props.model.controllerRegisterName)) {
      const rn = trim(d.CORPORATE_ENTITY_REGISTER ?? d.corporateEntityRegister);
      if (rn) props.model.controllerRegisterName = rn;
    }
    syncControllerIdentityFields(contactRegNo);
  } catch (e) {
    console.warn('[NoticeCorporateForm] contact detail fetch failed:', e);
  }
}

const CORPORATE_LOOKUP_GROUPS = new Set(['corporateController', 'noticeRecipient']);

watch(
  () => props.selectedPerson,
  async (person) => {
    if (!person) return;
    if (!b15JurisdictionMap.value.size) {
      b15JurisdictionMap.value = await ensureB15JurisdictionNameMap();
    }
    const fieldGroup = String(props.activePersonContext?.fieldGroup || '');
    if (fieldGroup === 'controllerJurisdictionB15') {
      applySelectedJurisdiction(person as unknown as Record<string, unknown>);
      return;
    }
    if (fieldGroup === 'noticeSigner') {
      applySelectedSecretary(person);
      return;
    }
    if (fieldGroup === 'acknowledgementSigner') {
      applyAcknowledgementSigner(person);
      return;
    }
    if (!CORPORATE_LOOKUP_GROUPS.has(fieldGroup)) return;
    applyCorporateController(person);
    await hydrateCorporateContactIfMissing(person);
  },
);

watch(
  () => props.model.noticeRecipientName,
  (name, prev) => {
    const next = trim(name);
    const corporate = trim(props.model.controllerCorporateName);
    const previous = trim(prev);
    if (!next) return;
    if (!corporate || corporate === previous) props.model.controllerCorporateName = next;
  },
);

watch(
  () => props.model.controllerCorporateName,
  (name, prev) => {
    const next = trim(name);
    const recipient = trim(props.model.noticeRecipientName);
    const previous = trim(prev);
    if (!next) return;
    if (!recipient || recipient === previous) props.model.noticeRecipientName = next;
  },
);

watch(
  () => props.model,
  () => {
    if (requiredErrorKeys.value.size === 0) return;
    setRequiredErrors(firstMissingRequiredKeys());
  },
  { deep: true },
);
</script>

<style scoped>
.ad-form { display: flex; flex-direction: column; gap: 0; }
.ad-section {
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #e9edf5;
  border-radius: 12px;
  padding: 0 0 12px;
  overflow: hidden;
}
.ad-section-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #283593;
  margin: 0 0 12px;
  padding: 10px 14px;
  background: linear-gradient(180deg, #eef2ff 0%, #e8ecfb 100%);
  border-bottom: 1px solid #d6dcf2;
}
.ad-section > .v-row { padding-left: 14px; padding-right: 14px; }
.ad-section > :not(.ad-section-header):not(.v-row) { padding-left: 14px; padding-right: 14px; }
.ad-required-mark { color: #e53935; }

.ad-suggest-chip {
  cursor: pointer;
  font-size: 11px !important;
  background: #ffffff !important;
  color: #212121 !important;
  border-color: #ffb300 !important;
  border-width: 1px !important;
}

.ad-suggest-chip:hover {
  background: #fff8e1 !important;
}

.ad-suggest-chip :deep(.v-chip__content) {
  color: #212121 !important;
}

.ad-suggest-chip :deep(.v-icon) {
  color: #f57c00 !important;
}

.ad-suggest-chip :deep(.v-chip__close) {
  color: #757575 !important;
  opacity: 1;
}

.ad-suggest-chip :deep(.v-chip__close:hover) {
  color: #424242 !important;
}
</style>
